import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { css } from '@emotion/react';
import {
  alpha,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useState } from 'react';
import { FooterButtons } from '../../components/Dialog/ConfirmDialog/FooterButtons';
import { DIALOG_PX, dialogInputSx, dialogPaperSx } from './sourcing-dialog.styles';

interface CreateListDialogProps {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (name: string, type: 'company' | 'person') => void;
}

export const CreateListDialog: FC<CreateListDialogProps> = ({ open, loading, onCancel, onConfirm }) => {
  const { colors } = useTheme();
  const [listType, setListType] = useState<'company' | 'person'>('company');
  const [name, setName] = useState('');

  const infoBannerCss = css`
    display: flex;
    align-items: center;
    gap: 4px;
    background-color: ${colors.primary[5]};
    border-radius: 4px;
    padding: 16px 12px;
  `;

  const reset = () => {
    setListType('company');
    setName('');
  };

  const handleClose = () => {
    onCancel();
  };

  const handleConfirm = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    onConfirm(trimmed, listType);
  };

  const nameLabel = listType === 'company' ? 'Company List' : 'People List';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      TransitionProps={{ onExited: reset }}
      slotProps={{ paper: { sx: dialogPaperSx } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 16,
          letterSpacing: '0.15px',
          lineHeight: 1.5,
          color: alpha(colors.primary[100], 0.87),
          pt: '16px',
          pb: '10px',
          mb: '12px',
          px: DIALOG_PX,
        }}
      >
        Create List
        <IconButton size='small' aria-label='Close' onClick={handleClose}>
          <CloseIcon sx={{ fontSize: 20, color: colors.neutral[40] }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '12px', px: DIALOG_PX }}>
        <div>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: '4px' }}>
            List Type
          </Typography>
          <ToggleButtonGroup
            exclusive
            color='secondary'
            value={listType}
            onChange={(_e, val) => {
              if (val !== null) {
                setListType(val);
              }
            }}
          >
            <ToggleButton value='company'>Company List</ToggleButton>
            <ToggleButton value='person'>People List</ToggleButton>
          </ToggleButtonGroup>
        </div>

        <div>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: '4px' }}>
            {nameLabel} Name <span style={{ color: colors.critical[50] }}>*</span>
          </Typography>
          <OutlinedInput
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirm();
              }
            }}
            sx={dialogInputSx}
          />
        </div>

        <div css={infoBannerCss}>
          <ErrorOutlineIcon sx={{ fontSize: 20, color: alpha(colors.neutral[70], 0.64) }} />
          <Typography
            variant='body2'
            color={alpha(colors.primary[100], 0.87)}
            sx={{ lineHeight: 'normal', letterSpacing: '0.17px' }}
          >
            This list will be visible to everyone in your organization. All users can view and edit it.
          </Typography>
        </div>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-start', gap: '4px', py: '24px', px: DIALOG_PX }}>
        <FooterButtons
          confirmButtonText='Create'
          onConfirm={handleConfirm}
          onCancel={handleClose}
          onConfirmDisabled={!name.trim()}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};
